/**
 * V3 ↔ V4 name translations for scenario triggers and actions.
 * V3 uses camelCase (`allAlarmTypes`, `sms`); V4 uses snake_case
 * (`any_alarm`, `sms_message`). Several V3 triggers collapse to one V4
 * source — the inverse map picks the first V3 alias as canonical.
 */
import { scenarioTriggers } from '../../utils/models/scenarios/scenarioTriggers';
import { scenarioActionTypes } from '../../utils/models/ScenarioAction';
import { isKnownV4ActionType, isKnownV4TriggerSource } from './dictionaries';

export const v3ToV4TriggerSource = {
    [scenarioTriggers.allAlarmTypes]: 'any_alarm',
    [scenarioTriggers.genericAlarm]: 'any_alarm',
    [scenarioTriggers.lowPrioAlarm]: 'low_priority',
    [scenarioTriggers.sosButton]: 'sos_button',
    [scenarioTriggers.manDown]: 'man_down',
    [scenarioTriggers.ripcord]: 'ripcord',
    [scenarioTriggers.timer]: 'timer',
    [scenarioTriggers.configError]: 'config_error',
    [scenarioTriggers.alarmClosed]: 'alarm_closed',
    [scenarioTriggers.connectionChanged]: 'connection_change',
    [scenarioTriggers.quickButton]: 'quick_button',
    [scenarioTriggers.beaconBattery]: 'beacon_battery',
    [scenarioTriggers.geofenceAttendance]: 'geofence_attendance',
    [scenarioTriggers.geofenceEntered]: 'geofence_enter',
    [scenarioTriggers.geofenceExited]: 'geofence_exit',
    [scenarioTriggers.docked]: 'docked',
    [scenarioTriggers.undocked]: 'undocked',
    [scenarioTriggers.lowBattery]: 'low_battery',
    [scenarioTriggers.noAlarmTriggered]: 'no_alarm_triggered',
    [scenarioTriggers.webhook]: 'webhook',
    [scenarioTriggers.multiTenant]: 'multi_tenant',
    [scenarioTriggers.messageNotAcknowledged]: 'message_not_acknowledged',
    [scenarioTriggers.checkAssistanceRequestResponses]: 'too_little_turnout_for_assistance_request',
    [scenarioTriggers.arcAlarm]: 'any_alarm',
    [scenarioTriggers.scenarioDisabled]: 'scenario_disabled',
};

export const v3ToV4ActionType = {
    [scenarioActionTypes.sms]: 'sms_message',
    [scenarioActionTypes.email]: 'email',
    [scenarioActionTypes.voiceMessage]: 'voice_message',
    [scenarioActionTypes.osm]: 'text_message_on_device',
    [scenarioActionTypes.startTracking]: 'start_tracking',
    [scenarioActionTypes.changeTeam]: 'change_team',
    [scenarioActionTypes.webhook]: 'call_webhook',
    [scenarioActionTypes.assistanceRequest]: 'send_assistance_request',
    [scenarioActionTypes.sendTelegramMessage]: 'send_telegram_message',
    [scenarioActionTypes.multiTenant]: 'multi_tenant',
    [scenarioActionTypes.resendLastOSM]: 'send_last_onscreen_message',
    [scenarioActionTypes.toggleTimer]: 'toggle_timer_alarm',
    // Unmapped, the ARC-link scenario's action stays `send_to_arc` and
    // ActionItem finds no icon, label or editor — a blank row.
    [scenarioActionTypes.sendToARC]: 'send_to_arc',
    [scenarioActionTypes.toggleScenario]: 'toggle_scenario',
};

// Already-V4 values pass through unchanged so V4-sourced scenarios round-trip.
export function toV4TriggerSource(v3OrV4) {
    if (!v3OrV4) return v3OrV4;
    return v3ToV4TriggerSource[v3OrV4] || v3OrV4;
}

export function toV4ActionType(v3OrV4) {
    if (!v3OrV4) return v3OrV4;
    return v3ToV4ActionType[v3OrV4] || v3OrV4;
}

// First V3 alias wins — gives the canonical V3 name for each V4 value.
function buildInverse(map) {
    const out = {};
    for (const [v3, v4] of Object.entries(map)) {
        if (!(v4 in out)) out[v4] = v3;
    }
    return out;
}
export const v4ToV3TriggerSource = buildInverse(v3ToV4TriggerSource);
export const v4ToV3ActionType = buildInverse(v3ToV4ActionType);

export function toV3TriggerSource(v4OrV3) {
    if (!v4OrV3) return v4OrV3;
    return v4ToV3TriggerSource[v4OrV3] || v4OrV3;
}

export function toV3ActionType(v4OrV3) {
    if (!v4OrV3) return v4OrV3;
    return v4ToV3ActionType[v4OrV3] || v4OrV3;
}

/**
 * Sources V4 accepts but whose config can't be produced here. `TriggerConfig`
 * now has a `code` field, but V4 still has no webhook ingress, so the scenario
 * could never show a URL to call. The mapping above stays so an
 * externally-created V4 webhook scenario still round-trips its source.
 */
const triggersNotEditableInV4 = [scenarioTriggers.webhook];

/**
 * True when a V3 trigger can be picked for a V4 scenario. No if it is listed
 * above, if it is a collapsing alias (genericAlarm / arcAlarm both → any_alarm,
 * so only allAlarmTypes shows), or if this V4 instance does not accept the
 * source — older V4 builds have no low_priority, and the picker must follow.
 */
export function isV3TriggerEditableInV4(v3Trigger) {
    if (triggersNotEditableInV4.indexOf(v3Trigger) >= 0) return false;
    const v4 = v3ToV4TriggerSource[v3Trigger];
    if (!v4) return false;
    if (v4ToV3TriggerSource[v4] !== v3Trigger) return false;
    return isKnownV4TriggerSource(v4);
}

// Offered only where the live V4 dictionary lists them.
const actionTypesNeedingV4Dictionary = [scenarioActionTypes.toggleScenario];

export function isV3ActionTypeEditableInV4(v3ActionType) {
    const v4 = v3ToV4ActionType[v3ActionType];
    if (!v4) return false;
    if (v4ToV3ActionType[v4] !== v3ActionType) return false;
    if (actionTypesNeedingV4Dictionary.indexOf(v3ActionType) >= 0) {
        return isKnownV4ActionType(v4);
    }
    return true;
}
